import React from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { GeocodedResult } from '@/hooks/useLocationSearch';

// ===========================================================================
// Autocomplete dropdown — floating beneath a location input (name or address)
// ===========================================================================
interface AutocompleteDropdownProps {
  open: boolean;
  isSearching: boolean;
  error: string | null;
  results: GeocodedResult[];
  onSelect: (result: GeocodedResult) => void;
}

export const AutocompleteDropdown: React.FC<AutocompleteDropdownProps> = ({
  open,
  isSearching,
  error,
  results,
  onSelect
}) => {
  if (!open) return null;
  if (!isSearching && !error && results.length === 0) return null;

  return (
    <div
      className={cn(
        'absolute top-full left-0 right-0 mt-1 z-20',
        'bg-popover border rounded-md shadow-lg max-h-64 overflow-y-auto'
      )}
    >
      {isSearching && (
        <div className='flex items-center gap-2 p-3 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Searching…
        </div>
      )}
      {!isSearching && error && <p className='p-3 text-sm text-destructive'>{error}</p>}
      {!isSearching &&
        results.map(result => (
          <button
            type='button'
            key={`${result.lat}-${result.lng}`}
            onMouseDown={e => e.preventDefault()}
            onClick={() => onSelect(result)}
            className={cn(
              'flex items-start gap-2 p-3 w-full text-left',
              'hover:bg-muted/50 border-b last:border-0'
            )}
          >
            <MapPin className='h-4 w-4 text-muted-foreground mt-0.5 shrink-0' />
            <div className='flex-1 min-w-0'>
              <p className='font-medium text-sm truncate'>
                {result.businessName || result.displayName.split(',')[0]}
              </p>
              <p className='text-xs text-muted-foreground break-words'>{result.displayName}</p>
            </div>
          </button>
        ))}
    </div>
  );
};
